import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  appointmentIdPayload,
  readAppointmentIdFromSearch,
} from "../../../../../utils/appointmentLinkContext";
import baseUrl from "../../../../../api/baseUrl";
import getDecryptedToken from "../../../../../api/getDecryptedToken";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { insertBreaks } from "../../../../../utils/functions/resize";
import {
  isTreatmentItemDeletable,
  isTreatmentItemEditable,
  isTreatmentItemPaid,
} from "./treatmentItemUtils";
import { toLocalYmd } from "../../../../../utils/colomboDate";

const user: string | undefined = getDecryptedToken("userName");
function AllMainTreatmentTr({
  item,
  onClick,
  refresh,
  handleEditTreatment,
  onAddSubTreatment,
  isSubFormOpen,
}: any) {
  const dateNow = new Date();
  const { ID } = useParams();
  const [searchParams] = useSearchParams();
  // Visit context from the URL; sent with status writes (see AllSubTreatmentTr).
  const appointmentId = readAppointmentIdFromSearch(searchParams);

  const [status, setstatus] = useState(item?.status || "Complete");
  const [planStatus, setPlanStatus] = useState(item?.planStatus || "planned");
  const isLongTerm = status === "long term";
  const isSuperAdmin = getDecryptedToken("role") === "Super Admin";
  const canEdit = isTreatmentItemEditable(item, isSuperAdmin);
  const canDelete = isTreatmentItemDeletable(item);

  useEffect(() => {
    setstatus(item?.status || "Complete");
  }, [item?.status]);

  useEffect(() => {
    setPlanStatus(item?.planStatus || "planned");
  }, [item?.planStatus]);

  const handleSelect = (e: any) => {
    if (item?.status === "long term") return;
    if (item?._id) {
      setstatus(e.target.value);
      baseUrl
        .post(
          "treatment/add-TreatmentStatus",
          { status: e.target.value, ...appointmentIdPayload(appointmentId) },
          {
            headers: {
              treatmentid: ID,
              treatmentsid: item._id,
            },
          }
        )
        .then((response) => {
          refresh();
        })
        .catch((error) => {
          console.log(error);
        });
      
    }
  };

  const handlePlanStatusSelect = (e: any) => {
    if (!item?._id) return;
    const next = e.target.value;
    setPlanStatus(next);
    baseUrl
      .post(
        "treatment/add-TreatmentStatus",
        { planStatus: next, ...appointmentIdPayload(appointmentId) },
        {
          headers: {
            treatmentid: ID,
            treatmentsid: item._id,
          },
        }
      )
      .then(() => {
        refresh();
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const handleSelectClick = () => {
    if (item?._id === undefined) {
      refresh();
    }
  };

  // const now = new Date();
  // const offset = now.getTimezoneOffset(); // in minutes
  // const hours = Math.floor(Math.abs(offset) / 60);
  // const minutes = Math.abs(offset) % 60;
  // const gmtSign = offset <= 0 ? '+' : '-';
  // console.log(`GMT${gmtSign}${hours}:${minutes < 10 ? '0' + minutes : minutes}`);

  return (
    <tr className="bg-[#fffbeb]/40 dark:bg-amber-900/10 hover:bg-amber-50/70 dark:hover:bg-amber-900/20 transition-colors">
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {toLocalYmd(item?.createdAt) || toLocalYmd(dateNow) || "—"}
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200">
        <div className="flex flex-wrap items-center gap-1.5">
          <span>
            {item?.treatment}
            {item?.type ? " - " + item?.type : ""}
            {item?.subtype ? " - " + item?.subtype : ""}
          </span>
          {item?.draft === true && (
            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
              Draft
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {item?.toothNos || item?.toothNo}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{item?.quantity}</td>
      <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200">
        {isLongTerm ? (
          <span className="text-violet-700 dark:text-violet-300" title="Treatment Plan estimation">
            Rs {item?.price}
          </span>
        ) : (
          <>Rs {item?.price}</>
        )}
      </td>
      <td className="px-4 py-3" onClick={handleSelectClick}>
        {isLongTerm ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-block px-2 py-1 rounded-lg text-xs font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 cursor-default">
              Treatment Plan
            </span>
            <select
              value={planStatus}
              onChange={handlePlanStatusSelect}
              className={`px-2 py-1 rounded-lg text-xs font-medium border-0 cursor-pointer ${
                planStatus === "Complete"
                  ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                  : planStatus === "ongoing"
                  ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                  : "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300"
              }`}
            >
              <option value="planned">Plan</option>
              <option value="ongoing">Ongoing</option>
              <option value="Complete">Complete</option>
            </select>
          </div>
        ) : (
          <select
            value={status}
            onChange={handleSelect}
            className={`px-2 py-1 rounded-lg text-xs font-medium border-0 cursor-pointer ${
              status === "Complete"
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                : status === "ongoing"
                ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                : "bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300"
            }`}
          >
            {!isTreatmentItemPaid(item) && <option value="planned">Planned</option>}
            <option value="ongoing">Ongoing</option>
            <option value="Complete">Complete</option>
          </select>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {item?.didBy
          ? item?.didBy?.firstName + " " + (item?.didBy?.lastName ?? "")
          : user}
      </td>
      <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 max-w-[150px] truncate">{insertBreaks(item?.note)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {onAddSubTreatment && (
            <button
              type="button"
              onClick={onAddSubTreatment}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                isSubFormOpen
                  ? "bg-primary text-white shadow-md"
                  : "bg-primary/90 text-white hover:bg-primary shadow-sm"
              }`}
              title="Add sub treatment"
            >
              <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => handleEditTreatment(item)}
              className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 flex items-center justify-center transition-colors"
              title={
                isTreatmentItemPaid(item)
                  ? "Edit paid treatment (Super Admin)"
                  : "Edit treatment"
              }
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </button>
          )}
          {canDelete && (
            <button
              onClick={onClick}
              className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 flex items-center justify-center transition-colors"
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default AllMainTreatmentTr;
